import { useMemo } from 'react';

interface ValidationIssue {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

interface EstimateData {
  customer_id?: string | null;
  title?: string;
  status?: string;
}

interface LineItem {
  id: string;
  description: string;
  quantity_in_base_units: number;
  total_cost_snapshot: number;
}

interface ValidationResult {
  isValid: boolean;
  canApprove: boolean;
  canSend: boolean;
  issues: ValidationIssue[];
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

export const useEstimateValidation = (
  estimate: EstimateData | null | undefined,
  lineItems: LineItem[] | null | undefined
): ValidationResult => {
  return useMemo(() => {
    const issues: ValidationIssue[] = [];

    // Check for customer
    if (!estimate?.customer_id) {
      issues.push({
        field: 'customer_id',
        message: 'A customer must be assigned before sending or approving this estimate.',
        severity: 'error',
      });
    }

    // Check for title
    if (!estimate?.title?.trim()) {
      issues.push({
        field: 'title',
        message: 'The estimate must have a title.',
        severity: 'error',
      });
    }

    // Check for line items
    if (!lineItems || lineItems.length === 0) {
      issues.push({
        field: 'lineItems',
        message: 'At least one line item is required.',
        severity: 'error',
      });
    }

    // Check for zero-quantity items
    const zeroQuantityItems = lineItems?.filter(item => item.quantity_in_base_units <= 0) || [];
    if (zeroQuantityItems.length > 0) {
      issues.push({
        field: 'lineItems',
        message: `${zeroQuantityItems.length} item(s) have zero or negative quantity.`,
        severity: 'warning',
      });
    }

    // Check for zero-cost items
    const zeroCostItems = lineItems?.filter(item => item.total_cost_snapshot <= 0) || [];
    if (zeroCostItems.length > 0) {
      issues.push({
        field: 'lineItems',
        message: `${zeroCostItems.length} item(s) have zero or negative cost.`,
        severity: 'warning',
      });
    }

    // Calculate total
    const total = lineItems?.reduce((sum, item) => sum + (item.total_cost_snapshot || 0), 0) || 0;
    if (total <= 0 && lineItems && lineItems.length > 0) {
      issues.push({
        field: 'total',
        message: 'The estimate total is zero or negative.',
        severity: 'warning',
      });
    }

    const errors = issues.filter(i => i.severity === 'error');
    const warnings = issues.filter(i => i.severity === 'warning');

    return {
      isValid: errors.length === 0,
      canApprove: errors.length === 0,
      canSend: errors.length === 0,
      issues,
      errors,
      warnings,
    };
  }, [estimate, lineItems]);
};

export default useEstimateValidation;
